import React, { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { submitCustomRequest } from '../lib/api';
import { ClientInfoSection, EventSection } from './custom-request/ClientEventSections';
import { ItemsSection, DesignDirectionSection } from './custom-request/DesignSections';
import { QuantitySection, BudgetSection, TimelineSection, TermsSection, BUDGET_OPTIONS } from './custom-request/LogisticsSections';

const INITIAL_FORM = {
  fullName: '',
  email: '',
  phone: '',
  contactMethod: 'email',
  eventType: '',
  eventDate: '',
  rushOrder: 'no',
  items: [],
  deliveryType: 'digital',
  theme: '',
  colors: '',
  style: 'not-sure',
  specificElements: '',
  quantity: '',
  size: '',
  budget: BUDGET_OPTIONS[0],
  priority: 'staying-within-budget',
  deadline: '',
  additionalInfo: '',
  agreedToTerms: false,
};

const CustomRequestForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  const set = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.agreedToTerms) {
      toast.error('Please agree to the terms before submitting');
      return;
    }

    setSubmitting(true);
    try {
      await submitCustomRequest(formData);
      toast.success("Custom request submitted! We'll be in touch within 24 hours.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleItemToggle = (item) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.includes(item)
        ? prev.items.filter((i) => i !== item)
        : [...prev.items, item],
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-warm-ivory">
        <DialogHeader>
          <DialogTitle className="text-3xl text-charcoal-ink">THE FAVOR</DialogTitle>
          <DialogTitle className="text-2xl text-charcoal-ink mt-2">Custom Design Request Form</DialogTitle>
          <DialogDescription className="text-stone-taupe mt-4">
            Have something specific in mind? The Favor offers fully customized party and event designs tailored to your vision.
            Please complete the form below with as much detail as possible.
            <br /><br />
            <strong className="text-charcoal-ink">Custom design projects start at $50 and may increase based on scope, quantity, and print options.</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-8 mt-6">
          <ClientInfoSection formData={formData} set={set} />
          <EventSection formData={formData} set={set} />
          <ItemsSection formData={formData} set={set} onItemToggle={handleItemToggle} />
          <DesignDirectionSection formData={formData} set={set} />
          <QuantitySection formData={formData} set={set} />
          <BudgetSection formData={formData} set={set} />
          <TimelineSection formData={formData} set={set} />
          <TermsSection formData={formData} set={set} />

          <div className="flex gap-4 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-stone-taupe/20 text-charcoal-ink hover:bg-soft-linen"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="custom-request-submit-button"
              className="flex-1 bg-charcoal-ink hover:bg-charcoal-ink/90 text-warm-ivory"
            >
              {submitting ? 'Submitting...' : 'Submit Custom Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CustomRequestForm;
